/**
 * Next-world destination thumbnail placement on Explore Unknown map surfaces.
 * Coordinates are authoring space (1920×1080), top-left of the preview foreignObject.
 */
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from '../exploreUnknownPortalLayout.js';
import { loadDevWaypoints } from './exploreUnknownWaypointDevStorage.js';

export const EXIT_PREVIEW_FOREIGN_WIDTH = 92;
export const EXIT_PREVIEW_FOREIGN_HEIGHT = 82;
const EXIT_PREVIEW_WAYPOINT_GAP = 14;
const EXIT_PREVIEW_FACTION_WAYPOINT_RADIUS = 22;
const EXIT_PREVIEW_BOSS_WAYPOINT_RADIUS = 36;

/** @typedef {'normal'|'altMinus1'|'altMinus2'} ExitPreviewRouteVariant */

/**
 * @param {object | null | undefined} row
 * @param {ExitPreviewRouteVariant} variant
 * @returns {number[] | null}
 */
function readLayoutExitPreviewPoint(row, variant) {
  if (!row || typeof row !== 'object') {
    return null;
  }
  let point;
  if (variant === 'normal') {
    point = row.normal;
  } else if (variant === 'altMinus1') {
    point = row.altMinus1 ?? row.alternative;
  } else {
    point = row.altMinus2;
  }
  if (Array.isArray(point) && point.length === 2) {
    return [point[0], point[1]];
  }
  return null;
}

/**
 * @param {1|2} missionsRemoved
 * @returns {ExitPreviewRouteVariant}
 */
export function resolveExitPreviewVariantForMissionsRemoved(missionsRemoved) {
  return missionsRemoved === 2 ? 'altMinus2' : 'altMinus1';
}

/**
 * @param {ExitPreviewRouteVariant} variant
 * @returns {'exitPreviewNormal'|'exitPreviewAltMinus1'|'exitPreviewAltMinus2'}
 */
export function exitPreviewDevPathModeForVariant(variant) {
  if (variant === 'altMinus2') {
    return 'exitPreviewAltMinus2';
  }
  if (variant === 'altMinus1') {
    return 'exitPreviewAltMinus1';
  }
  return 'exitPreviewNormal';
}

/**
 * @param {string} pathMode
 * @returns {boolean}
 */
export function isExitPreviewDevPathMode(pathMode) {
  return (
    pathMode === 'exitPreviewNormal' ||
    pathMode === 'exitPreviewAltMinus1' ||
    pathMode === 'exitPreviewAltMinus2' ||
    pathMode === 'exitPreviewAlternative'
  );
}

/**
 * @param {string} devPathEditMode
 * @returns {ExitPreviewRouteVariant}
 */
export function exitPreviewVariantFromDevPathMode(devPathEditMode) {
  if (devPathEditMode === 'exitPreviewAltMinus2') {
    return 'altMinus2';
  }
  if (devPathEditMode === 'exitPreviewAltMinus1' || devPathEditMode === 'exitPreviewAlternative') {
    return 'altMinus1';
  }
  return 'normal';
}

/**
 * @param {number[][]} points — SVG or authoring points along the route
 * @param {number} pointIndex
 * @param {{ variant?: 'main'|'alt', isBoss?: boolean }} [options]
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export function computeExitPreviewPlacementFromRoute(points, pointIndex, options = {}) {
  const point = points[pointIndex];
  if (!Array.isArray(point) || point.length !== 2) {
    return null;
  }
  const [wx, wy] = point;
  const variant = options.variant === 'alt' ? 'alt' : 'main';
  const waypointRadius =
    options.isBoss === true ? EXIT_PREVIEW_BOSS_WAYPOINT_RADIUS : EXIT_PREVIEW_FACTION_WAYPOINT_RADIUS;

  let dx = 0;
  let dy = 0;
  if (pointIndex > 0) {
    const [px, py] = points[pointIndex - 1];
    dx = wx - px;
    dy = wy - py;
  } else if (pointIndex + 1 < points.length) {
    const [nx, ny] = points[pointIndex + 1];
    dx = nx - wx;
    dy = ny - wy;
  } else {
    dx = 1;
    dy = 0;
  }
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;

  const previewProjection =
    (Math.abs(ux) * EXIT_PREVIEW_FOREIGN_WIDTH + Math.abs(uy) * EXIT_PREVIEW_FOREIGN_HEIGHT) * 0.5;
  const forwardDistance = waypointRadius + EXIT_PREVIEW_WAYPOINT_GAP + previewProjection;

  let centerX = wx + ux * forwardDistance;
  let centerY = wy + uy * forwardDistance;

  if (variant === 'alt') {
    const perpScale = EXIT_PREVIEW_FOREIGN_WIDTH * 0.32;
    centerX += -uy * perpScale;
    centerY += ux * perpScale;
  }

  return {
    x: centerX - EXIT_PREVIEW_FOREIGN_WIDTH / 2,
    y: centerY - EXIT_PREVIEW_FOREIGN_HEIGHT / 2,
    width: EXIT_PREVIEW_FOREIGN_WIDTH,
    height: EXIT_PREVIEW_FOREIGN_HEIGHT,
  };
}

/**
 * @param {number[]} authoringPoint — top-left in 1920×1080 space
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function placementRectFromAuthoringPoint(authoringPoint) {
  if (!Array.isArray(authoringPoint) || authoringPoint.length !== 2) {
    throw new Error('placementRectFromAuthoringPoint: point must be [x, y]');
  }
  return {
    x: authoringPoint[0],
    y: authoringPoint[1],
    width: EXIT_PREVIEW_FOREIGN_WIDTH,
    height: EXIT_PREVIEW_FOREIGN_HEIGHT,
  };
}

/**
 * @param {string} surface
 * @param {ExitPreviewRouteVariant} variant
 * @param {number[] | null | undefined} devOverridePoint
 * @returns {number[] | null}
 */
export function resolveRouteExitPreviewAuthoringPoint(surface, variant, devOverridePoint = undefined) {
  if (Array.isArray(devOverridePoint) && devOverridePoint.length === 2) {
    return [devOverridePoint[0], devOverridePoint[1]];
  }
  if (process.env.NODE_ENV === 'development') {
    try {
      const pathMode = exitPreviewDevPathModeForVariant(variant);
      const stored = loadDevWaypoints(surface, pathMode);
      if (stored?.[0]) {
        return [stored[0][0], stored[0][1]];
      }
      if (variant === 'altMinus1') {
        const legacy = loadDevWaypoints(surface, 'exitPreviewAlternative');
        if (legacy?.[0]) {
          return [legacy[0][0], legacy[0][1]];
        }
      }
    } catch {
      /* fall through to bundled layout */
    }
  }
  const row = EXPLORE_UNKNOWN_PORTAL_LAYOUT?.routeExitPreviewBySurface?.[surface];
  return readLayoutExitPreviewPoint(row, variant);
}
